<script setup>
    import { useDisclosureCounselingReserveStore } from '@/stores/modal/disclosureCounselingReserveStore';
    import { defineEmits, defineExpose, inject, ref, computed } from 'vue';
    import BaseSelect from '@/components/BaseSelectComponent.vue';
    import Utils from '@/commonProject/helpers/utils.js';
    import i18n from '@/common/i18n';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    /**
     * 対象者の選択の場合は1、報告先は3
     */
    const USER_SELECT_TYPE_1 = '1';
    //const USER_SELECT_TYPE_2 = '2';
    const USER_SELECT_TYPE_3 = '3';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // ストア取得
    const disclosureCounselingReserveStore = useDisclosureCounselingReserveStore();

    const form = ref({
        ServicePk: '',
        ClientUserPk: '',
        ReportingUserPks: [],
        SessionGrpApplyNote: '',
    });

    const ClientUserNm = ref('');
    const ReportingUser = ref([]);

    // 開示カウンセリング申込入力モーダルの表示・非表示を管理する。
    const disclosureCounselingReserveDispFlg = ref('');
    // 開示カウンセリング申込確認モーダルの表示・非表示を管理する。
    const disclosureCounselingConfirmDispFlg = ref('');
    // 開示カウンセリング申込完了モーダルの表示・非表示を管理る。
    const disclosureCounselingCompleteDispFlg = ref('');

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');

    // ダブルクリック対策
    const isPageDoubleClickBlocked = ref(false);

    // 呼び出し元の親画面の開示カウンセリング申込モーダルを閉じる処理を定義する。
    const emit = defineEmits(['disclosureCounselingReserveModal:close', 'disclosureCounselingReserveModal:userSelectOpen', 'disclosureCounselingReserveModal:updateAndClose']);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 開示カウンセリングサービス一覧取得処理
     */
    async function getServiceList() {
        const queryParam = {};

        const response = await disclosureCounselingReserveStore.getServiceListAction(API_URL.value, queryParam);

        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 開示カウンセリング申込確認モーダルを表示する。
     */
    async function reserveConfirmModal() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        disclosureCounselingReserveDispFlg.value = '';
        disclosureCounselingConfirmDispFlg.value = 'block';
        disclosureCounselingCompleteDispFlg.value = '';
    }

    /**
     * 開示カウンセリング申込確認モーダルの戻るボタンのクリックイベント。
     * 開示カウンセリング申込入力モーダルに戻る。
     */
    function backToReserveModal() {
        disclosureCounselingReserveDispFlg.value = 'block';
        disclosureCounselingConfirmDispFlg.value = '';
        disclosureCounselingCompleteDispFlg.value = '';
    }

    /**
     * 開示カウンセリング申込を実行し、開示カウンセリング申込完了モーダルを表示する。
     */
    async function reserveCompleteModal() {
        // ダブルクリックを無効化する。
        if (isPageDoubleClickBlocked.value) {
            return;
        }
   
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // ダブルクリック対策フラグを有効にする。
        isPageDoubleClickBlocked.value = true;

        // リクエストボディを生成する。
        const queryParam = {
            ServicePk: form.value.ServicePk,
            ClientUserPk: form.value.ClientUserPk,
            ReportingUserPks: selectService.value.ServiceResRep ? form.value.ReportingUserPks : [], // 報告先のユーザーが画面で選択されていない場合は、ユーザーPK配列は空で送信する。
            SessionGrpApplyNote: form.value.SessionGrpApplyNote,
        };

        // 開示カウンセリング登録APIをコールする。
        const response = await disclosureCounselingReserveStore.registDisclosureCounselingAction(API_URL.value, queryParam);
        if (response.success) {
            disclosureCounselingReserveDispFlg.value = '';
            disclosureCounselingConfirmDispFlg.value = '';
            disclosureCounselingCompleteDispFlg.value = 'block';
            reset();
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }

        // ダブルクリック対策フラグを初期化する。
        isPageDoubleClickBlocked.value = false;
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // サービス
        if (Utils.isNullEmpty(form.value.ServicePk)) {
            message += i18n.global.t('disclosureCounseling.message.message3') + '\n';
        }
        // 対象者
        if (Utils.isNullEmpty(form.value.ClientUserPk)) {
            message += i18n.global.t('disclosureCounseling.message.message4') + '\n';
        }
        // 選択したサービスが報告先の設定が必須か判定する。
        if (ServiceResRep.value) {
            if (Utils.isNullEmptyArray(ReportingUser.value)) {
                message += i18n.global.t('disclosureCounseling.message.message6') + '\n';
            }
        }
        // ご連絡
        if (!Utils.isNullEmpty(form.value.SessionGrpApplyNote)) {
            if (Utils.checkLengthOfLongString(form.value.SessionGrpApplyNote)) {
                message += i18n.global.t('disclosureCounseling.message.message5') + '\n';
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    /**
     * 開示カウンセリング申込入力モーダルの「キャンセル」ボタンイベント。
     * 開示カウンセリング申込モーダルを閉じて、親画面に戻る。
     */
    function closeDisclosureCounselingModal() {
        reset();
        // 親画面のキャンセル処理を呼び出す。
        emit('disclosureCounselingReserveModal:close');
    }

    /**
     * 開示カウンセリング申込完了モーダルの「閉じる」ボタンイベント。
     * 開示カウンセリング申込モーダルを閉じて、親画面に戻る。
     */
    function updateAndCloseDisclosureCounselingModal() {
        reset();
        // 親画面のキャンセル処理を呼び出す。
        emit('disclosureCounselingReserveModal:updateAndClose');
    }

    /**
     * 開示カウンセリング申込入力モーダルで対象者選択ボタンを押下した場合に親画面に戻る。
     */
    function userSelectOpen(type) {
        // 親画面の対象者選択ボタン押下処理を呼び出す。
        emit('disclosureCounselingReserveModal:userSelectOpen', type);
    }

    /**
     * 親画面から開示カウンセリング申込モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @returns なし
     */
    async function openModal(trg) {
        reset();

        // モーダルの表示を初期化する。入力モーダルの表示に切り替えて、他モーダルは全て閉じる。
        disclosureCounselingReserveDispFlg.value = 'block';
        disclosureCounselingConfirmDispFlg.value = '';
        disclosureCounselingCompleteDispFlg.value = '';

        // APIのURL情報を取得する。
        API_URL.value = trg;

        await getServiceList();

        // モーダルのHTML要素を取得する。
        const elm11 = document.querySelector('#modalOpenCounselingServiceEntry .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm11.scrollTop = 0;

        // モーダルのHTML要素を取得する。
        const elm12 = document.querySelector('#modalOpenCounselingServiceCheck .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm12.scrollTop = 0;
    }

    /**
     * 親画面から開示カウンセリング申込モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @returns なし
     */
    async function selectModal(UserPk, UserNm) {
        form.value.ClientUserPk = UserPk;
        ClientUserNm.value = UserNm;
    }

    /**
     * 親画面から開示カウンセリング申込モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @returns なし
     */
    async function addModal(UserPk, UserNm) {
        if (form.value.ReportingUserPks.find((data) => data === UserPk) === undefined) {
            form.value.ReportingUserPks.push(UserPk);
            ReportingUser.value.push({ Pk: UserPk, Nm: UserNm });
        }
    }

    /**
     * 親画面から開示カウンセリング申込モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @returns なし
     */
    async function deleteUser(UserPk) {
        form.value.ReportingUserPks = form.value.ReportingUserPks.filter((elem) => elem != UserPk);
        ReportingUser.value = ReportingUser.value.filter((elem) => elem.Pk != UserPk);
    }

    const selectService = ref('');
    // サービス内容変化時
    async function serviceChange() {
        // サービスの一覧から該当するサービスPKを記録する。
        selectService.value = disclosureCounselingReserveStore.ServiceList.find((select) => select.ServicePk === form.value.ServicePk);

        // 対象者の選択を初期化する。
        form.value.ClientUserPk = '';
        ClientUserNm.value = '';

        // 報告先の一覧の選択を初期化する。
        ReportingUser.value = [];
    }

    const ServiceNm = computed(() => {
        return selectService.value === undefined ? '' : selectService.value.ServiceNm;
    });

    const ServiceExpDsc = computed(() => {
        return selectService.value === undefined ? '' : selectService.value.ServiceExpDsc;
    });

    const ServiceResRep = computed(() => {
        return selectService.value === undefined ? false : selectService.value.ServiceResRep;
    });

    const clientUserNmTag = computed(() => {
        return ClientUserNm.value != '' ? 'span' : 'div';
    });

    /**
     * 初期化する。
     */
    function reset() {
        disclosureCounselingReserveStore.reset();
        form.value.ServicePk = '';
        form.value.ClientUserPk = '';
        form.value.ReportingUserPks = [];
        form.value.SessionGrpApplyNote = '';
        selectService.value = '';
        ClientUserNm.value = '';
        ReportingUser.value = [];

        // ダブルクリック対策フラグを初期化する。
        isPageDoubleClickBlocked.value = false;
    }

    /**
     * 親画面から実行を許可する本コンポーネント内のメソッドの一覧。
     */
    defineExpose({
        openModal,
        selectModal,
        addModal,
    });
</script>
<template>
    <article id="modalOpenCounselingServiceEntry" class="modal" :style="{ display: disclosureCounselingReserveDispFlg }">
        <h3 class="textCenter">{{ $t('disclosureCounseling.parts.pageTitle2') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <section class="form">
                <!-- サービス -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="select">
                                    <BaseSelect :options="disclosureCounselingReserveStore.SelectServiceList" v-model="form.ServicePk" required @childEmit="serviceChange" />
                                </div>
                                <p style="padding: 5px; border: 1px solid #adadad; width: 100%; min-height: 92px; border-radius: 4px; margin-bottom: 10px; box-sizing: border-box">{{ ServiceExpDsc }}</p>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 対象者 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader4') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd style="margin-top: 10px">
                        <ul class="formList listA">
                            <li>
                                <component :is="clientUserNmTag">{{ ClientUserNm }}</component>
                                <button type="button" class="btnSelect modeA" @click="userSelectOpen(USER_SELECT_TYPE_1)">{{ $t('disclosureCounseling.parts.button4') }}</button>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 報告先 -->
                <dl v-show="ServiceResRep" id="alarmBox">
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.dt2') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd style="margin-top: 10px">
                        <ul class="formList listB">
                            <li v-for="(user, index) in ReportingUser" :key="index">
                                <span>{{ user.Nm }}</span>
                                <button type="button" class="btnDelete modeB" @click="deleteUser(user.Pk)">{{ $t('disclosureCounseling.parts.button5') }}</button>
                            </li>
                            <li>
                                <button type="button" class="btnSelect modeB" @click="userSelectOpen(USER_SELECT_TYPE_3)">{{ $t('disclosureCounseling.parts.button6') }}</button>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 連絡 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.dt3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <textarea style="width: 100%; height: 98px; margin-top: 10px" v-model="form.SessionGrpApplyNote" />
                            </li>
                        </ul>
                    </dd>
                </dl>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal closeModal" @click="closeDisclosureCounselingModal">{{ $t('common.parts.cancel') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary" id="goToOpenCounselingServiceCheck" @click="reserveConfirmModal">{{ $t('disclosureCounseling.parts.button7') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--開示カウンセリング申込-->
    <article id="modalOpenCounselingServiceCheck" class="modal" :style="{ display: disclosureCounselingConfirmDispFlg }">
        <h3 class="textCenter">{{ $t('disclosureCounseling.parts.pageTitle2') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <section class="form dtWidth80 marginBottom15">
                <p class="pageInfo">{{ $t('disclosureCounseling.message.message1') }}</p>
                <!-- サービス -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span id="selectedService">{{ ServiceNm }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 対象者 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.tableHeader4') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd style="margin-top: 10px">
                        <ul class="formList">
                            <li>
                                <span>{{ ClientUserNm }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl id="alarmBoxChecl" v-show="ServiceResRep">
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.dt2') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd style="margin-top: 10px; margin-bottom: 10px">
                        <ul class="formList" v-for="(user, index) in ReportingUser" :key="index">
                            <li>
                                <span>{{ user.Nm }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 連絡 -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('disclosureCounseling.parts.dt3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <textarea disabled="" style="width: 100%; height: 98px; margin-top: 10px" v-model="form.SessionGrpApplyNote" />
                            </li>
                        </ul>
                    </dd>
                </dl>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" id="backToOpenCounselingServiceEntry" @click="backToReserveModal">{{ $t('common.parts.cancel') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary" id="goToModalKaitouTokurokuComplete" @click="reserveCompleteModal" :disabled="disabledStore.isDisabled">{{ $t('disclosureCounseling.parts.button8') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--開示カウンセリング申込確認-->
    <article id="modalKaitouTokurokuComplete" class="modal small" :style="{ display: disclosureCounselingCompleteDispFlg }">
        <p class="textCenter" style="padding-left: 70px; width: 300px; margin-bottom: 21px">{{ $t('disclosureCounseling.message.message2') }}</p>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal closeModal" @click="updateAndCloseDisclosureCounselingModal">{{ $t('disclosureCounseling.parts.modalButton3') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <!--開示カウンセリング申込完了-->
</template>

<style></style>
